import { useState, useEffect } from "react";

import AdminEggList from "./AdminEggList";
import PlantsTable from "../../Components/PlantsTable";
import AdminPreorderSummary from "./AdminPreorderSummary";
import AdminManageStoreItems from "./AdminManageStoreItems";
import AdminManageSeasons from "./AdminManageSeasons";
import AdminManageHours from "./AdminManageHours";
import AdminManageBanner from "./AdminManageBanner";
import AdminInvoices from "./AdminInvoices";
import AdminInventory from "./AdminInventory";
import AdminInventoryCount from "./AdminInventoryCount";
import AdminInventoryReports from "./AdminInventoryReports";
import { getImageUrl } from "../../utils/imageUrl";

import {
	Table,
	Box,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Button,
	CircularProgress,
	Typography,
	TextField,
	Grid,
	List,
	ListItem,
	ListItemText,
	ListItemButton,
	Tabs,
	Tab,
} from "@mui/material";

import { ArrowDropUp } from "@mui/icons-material";

import supabase from "../../supabaseClient";
import theme from "../../theme";
import useAdminAuth from "../../utils/useAdminAuth";
import AuthGate from "../../Components/AuthGate";
import { ArrowDownward } from "@mui/icons-material";
import RequestEggs from "./RequestEggs";
import AdminJobApplications from "../AdminApplications";

const Dashboard = () => {
	const { authChecked, isLoggedIn } = useAdminAuth();
	const [plants, setPlants] = useState([]);
	const [user, setUser] = useState(null);
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(true);

	const [selectedTab, setSelectedTab] = useState("Profile Settings");
	const [tabs, setTabs] = useState(["Profile Settings"]);

	const isSmallViewport = window.innerWidth < 600;
	const [open, setOpen] = useState(false);

	// Store admin tab state
	const [storeTab, setStoreTab] = useState(0);

	useEffect(() => {
		const fetchData = async () => {
			const {
				data: { session },
			} = await supabase.auth.getSession();

			const { data: userData, error: userError } = await supabase
				.from("profiles")
				.select("*")
				.eq("supabaseId", session?.user?.id)
				.single();
			if (userError) {
				setError(userError.message);
				setLoading(false);
				return;
			}
			setUser(userData);
			if (userData.role === "admin") {
				setTabs([
					"Plant List",
					"Egg Requests",
					"Store Details",
					"Inventory",
					"POS",
					"Job Applications",
					"Invoices",
					"Profile Settings",
				]);
			} else {
				setTabs(["Egg Requests", "Profile Settings"]);
				setLoading(false);
				return;
			}
			const { data: plantsData, error: plantsError } = await supabase
				.from("plants")
				.select("*")
				.order("created_at", { ascending: false });

			if (plantsError) {
				setError(plantsData.message);
				setLoading(false);
				return;
			}
			setPlants(plantsData);
			setLoading(false);
		};
		fetchData();
	}, []);

	// Show auth gate while checking or when access is denied
	if (!authChecked || (authChecked && !isLoggedIn)) {
		if (!authChecked) {
			return (
				<Box
					display="flex"
					justifyContent="center"
					alignItems="center"
					height="100vh"
					width="100vw"
				>
					<CircularProgress />
				</Box>
			);
		}
		return <AuthGate variant="not-logged-in" />;
	}

	if (loading)
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				height="100vh"
				width="100vw"
			>
				<CircularProgress />
			</Box>
		);
	if (error) return <div>{error}</div>;

	const renderList = () => {
		const isMobile = window.innerWidth < 600;

		const listItems = tabs.map((text) => (
			<ListItem
				button="true"
				key={text}
				selected={selectedTab === text}
				sx={{
					backgroundColor:
						selectedTab === text ? theme.palette.primary.main : null,
					color:
						selectedTab === text
							? theme.palette.primary.textWhite
							: theme.palette.primary.textBlack,
					"&:hover": {
						backgroundColor: theme.palette.primary.main,
						color: theme.palette.primary.textWhite,
					},
				}}
				onClick={() => setSelectedTab(text)}
			>
				<ListItemText primary={text} />
			</ListItem>
		));

		return (
			<List sx={{ padding: 0 }}>
				{isMobile && (
					<>
						{open && (
							<>
								{listItems}
								<ListItemButton onClick={() => setOpen(!open)}>
									<ListItemText primary="View Less" /> <ArrowDropUp />
								</ListItemButton>
							</>
						)}
						{!open && (
							<>
								<ListItemButton onClick={() => setOpen(!open)}>
									<ListItemText primary="View More" /> <ArrowDownward />
								</ListItemButton>
							</>
						)}
					</>
				)}
			</List>
		);
	};

	return (
		<Grid container>
			{isSmallViewport ? (
				<>
					<Grid item xs={12} sm={2}>
						<Paper
							sx={{
								height: "100%",
								borderRadius: 0,
							}}
						>
							{renderList()}
						</Paper>
					</Grid>
				</>
			) : (
				<Grid item xs={12} md={2}>
					<Paper
						sx={{
							borderRadius: 0,
						}}
					>
						<List>
							{tabs.map((text) => (
								<ListItemButton
									key={text}
									sx={{
										backgroundColor:
											selectedTab === text ? theme.palette.primary.main : null,
										color:
											selectedTab === text
												? theme.palette.primary.textWhite
												: theme.palette.primary.textBlack,
										"&:hover": {
											backgroundColor: theme.palette.primary.main,
											color: theme.palette.primary.textWhite,
										},
									}}
									onClick={() => setSelectedTab(text)}
								>
									<ListItemText primary={text} />
								</ListItemButton>
							))}
						</List>
					</Paper>
				</Grid>
			)}

			<Grid item xs={12} md={10}>
				<Box p={2}>
					{
						// show the plants table if the user is an admin
						// show the profile if the user is a customer
						selectedTab === "Plant List" && (
							<PlantsTable plants={plants} isAdmin={user?.role === "admin"} />
						)
					}
					{selectedTab === "Egg Requests" && user?.role === "admin" && (
						<Box>
							<AdminEggList />
						</Box>
					)}
					{selectedTab === "Job Applications" && user?.role === "admin" && (
						<Box>
							<AdminJobApplications />
						</Box>
					)}
					{selectedTab === "Invoices" && user?.role === "admin" && (
						<Box>
							<AdminInvoices />
						</Box>
					)}
					{selectedTab === "Egg Requests" && user?.role === "customer" && (
						<Box>
							<RequestEggs />
						</Box>
					)}
					{
						// show the plants table if the user is a customer
						selectedTab === "Plants" && user?.role === "customer" && (
							<Box>
								<Box
									display="flex"
									flexDirection="row"
									alignItems="center"
									p={2}
								>
									<h1>Plants</h1>
								</Box>
								<TableContainer component={Paper}>
									<Table>
										<TableHead>
											<TableRow>
												<TableCell>Variety</TableCell>
												<TableCell>Name</TableCell>
												<TableCell>Description</TableCell>
												<TableCell>Type</TableCell>
												<TableCell>Pot Size</TableCell>
												<TableCell>Photo</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{plants?.map((plant) => (
												<TableRow key={plant.id}>
													<TableCell>{`${plant.plant_variety}`}</TableCell>
													<TableCell>{`${plant.plant_name}`}</TableCell>
													<TableCell>{plant.plant_description}</TableCell>
													<TableCell>{plant.plant_type}</TableCell>
													<TableCell>{plant.pot_size}</TableCell>
													<TableCell>
														<Box display="flex" flexDirection="row">
															<Box p={1}>
																<Button
																	variant="contained"
																	sx={{
																		backgroundColor: theme.palette.primary.main,
																		color: "white",
																	}}
																	onClick={() => {
																		window.open(
																			`${getImageUrl(plant.photo_url)}`,
																			"_blank",
																		);
																	}}
																>
																	View
																</Button>
															</Box>
														</Box>
													</TableCell>

													{/* <TableCell></TableCell> */}
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						)
					}

					{selectedTab === "Profile Settings" && (
						//display the users profile details and allow them to edit their profile
						<Box>
							<h1>Profile</h1>
							<Box display="flex" flexDirection="column">
								<Box p={1}>
									<TextField
										label="Name"
										value={user?.first_name}
										disabled
										variant="outlined"
									/>
								</Box>
								<Box p={1}>
									<TextField
										label="Email"
										value={user?.email}
										disabled
										helperText="Contact us if you want to change your email"
										variant="outlined"
									/>
								</Box>
								<Box p={1}>
									<TextField
										label="Account Type"
										value={user?.role === "customer" ? "Customer" : "Admin"}
										disabled
										variant="outlined"
										helperText="You cannot edit your account type"
									/>
								</Box>
							</Box>
						</Box>
					)}
					{selectedTab === "Store Details" && user?.role === "admin" && (
						<Box>
							<Tabs
								value={storeTab}
								onChange={(_, v) => setStoreTab(v)}
								sx={{ mb: 2 }}
								variant="fullWidth"
							>
								<Tab label="Manage Store Items" />
								<Tab label="Preorder Summary" />
								<Tab label="Manage Seasons" />
								<Tab label="Manage Hours" />
								<Tab label="Header Banner" />
							</Tabs>
							{storeTab === 0 && (
								<Box>
									<AdminManageStoreItems />
								</Box>
							)}
							{storeTab === 1 && (
								<Box>
									<AdminPreorderSummary />
								</Box>
							)}
							{storeTab === 2 && (
								<Box>
									<AdminManageSeasons />
								</Box>
							)}
							{storeTab === 3 && (
								<Box>
									<AdminManageHours />
								</Box>
							)}
							{storeTab === 4 && (
								<Box>
									<AdminManageBanner />
								</Box>
							)}
						</Box>
					)}
					{selectedTab === "Inventory" && user?.role === "admin" && (
						<Box>
							<Tabs
								value={storeTab}
								onChange={(_, v) => setStoreTab(v)}
								sx={{ mb: 2 }}
								variant="scrollable"
								scrollButtons="auto"
							>
								<Tab label="Stock Levels" />
								<Tab label="Inventory Count" />
								<Tab label="Reports" />
							</Tabs>
							{storeTab === 0 && <AdminInventory />}
							{storeTab === 1 && <AdminInventoryCount />}
							{storeTab === 2 && <AdminInventoryReports />}
						</Box>
					)}
					{selectedTab === "POS" && user?.role === "admin" && (
						<Box>
							<Typography variant="h6" gutterBottom>
								Point of Sale
							</Typography>
							<Typography variant="body2" color="text.secondary" mb={2}>
								Use the full-screen POS for the best experience on tablets and
								phones.
							</Typography>
							<Button
								variant="contained"
								href="/pos"
								target="_blank"
								sx={{ mb: 2 }}
							>
								Open POS (full screen)
							</Button>
						</Box>
					)}
					{selectedTab === "Preorder Summary" && user?.role === "admin" && null}
					{selectedTab === "Manage Store Items" &&
						user?.role === "admin" &&
						null}
					{selectedTab === "Manage Seasons" && user?.role === "admin" && null}
				</Box>
			</Grid>
		</Grid>
	);
};

export default Dashboard;
